import type { AuthedRequester } from "@/lib/brokerAuth";

// The broker's in-app inbox seam — `/api/broker/notifications/**` (PMS-S353,
// pairs BRK-S157). Types mirror the PMS contract (docs/openapi.yaml). The inbox
// is the broker's `notification` rows where channel='in_app'; merely LISTING them
// marks the returned rows SEEN server-side, so the bell badge — the UNSEEN count —
// clears when the /notifications page opens. Timestamps are ISO strings on the
// wire (the PMS Date columns serialize to strings over JSON).

const BASE = "/api/broker/notifications";

/** One in-app notification (PMS InboxItem). */
export interface InboxItem {
  id: string;
  communicationId: string;
  communicationNumber: string;
  noticeType: string | null;
  purpose: string;
  subject: string;
  body: string | null;
  priorityTier: "critical" | "transactional" | "promotional";
  createdAt: string;
  seenAt: string | null;
  readAt: string | null;
  /**
   * WHICH record this notice is about (BRK-S177, consuming PMS-S466).
   *
   * ⚠️ READ AHEAD OF THE PIN. The inbox item publishes `noticeType` today and
   * nothing else about its subject, so a `bid_result_needed` row can say what
   * KIND of notice it is and not which order it chases — which is the one thing
   * the in-app half of that reminder needs in order to carry the same link the
   * EMAIL already does (PMS's `bidResultUrl`). The `communication` row already
   * holds the nullable typed FKs and the `subject_kind` discriminator
   * (PMS-S345), so PMS-S466 is a read-model widening rather than a schema
   * change.
   *
   * OPTIONAL and NULLABLE, which renders no link at all until it arrives, and
   * `test/lib/brokerNotificationsContract.test.ts` fails the day the shape is
   * pinned — so this note cannot outlive its own truth. `null` is a real value
   * too: an internal or account-wide notice is about no order.
   *
   * The POINTER id, never a discovery id — it is what
   * `/surety/submissions/{id}` takes.
   */
  bondSubmissionId?: string | null;
  /**
   * WHICH invoice an invoicing notice is about (BRK-S187).
   *
   * Read ahead of the pin first; published since the sprint-10 release re-pin
   * (PMS-S502), nullable. Present on `invoice_generated` and
   * `invoice_paid` this sprint; null on `invoice_due` / `ucc_warning_letter`
   * until the Sprint 11 ladder. Absent or null, the notice falls back to its
   * order's profile.
   */
  invoiceId?: string | null;
  /**
   * WHICH insured an ACCOUNT-level notice is about (BRK-S330 / BRK-S329) — the
   * Signal Advance and capacity decisions, which concern no order.
   *
   * Read ahead of the pin first; published since the sprint-10 release re-pin
   * (PMS-S723), nullable. Absent or null, an account notice falls
   * back to its order's link when it names one, else to no link.
   */
  accountId?: string | null;
  /**
   * WHICH commercial submission a notice is about (BRK-S466, consuming
   * PMS-S872): the quote-ready, bind, review, condition and action-item
   * notices, which concern no bond order. Published since the notifications
   * slice's first pin, nullable; absent or null, a commercial notice has no
   * link. What `/submissions/{id}` takes.
   */
  submissionId?: string | null;
  /** What the notice is about, as PMS discriminates it: a bond order, an
   *  account, or a commercial submission (PMS-S872). Read for completeness;
   *  the ids above decide the link. */
  subjectKind?: "bond_submission" | "account" | "submission" | null;
}

/** The small, closed set of pictures an inbox row can carry. */
export type NoticeIcon =
  | "update"
  | "bid"
  | "signature"
  | "packet"
  | "invoice"
  | "warning"
  | "paid"
  | "quote"
  | "bound"
  | "review"
  | "reply"
  | "condition";

/** What KIND of notice a row is, as a broker reads it: a short label and an
 *  icon, beside the subject PMS wrote. */
export interface NoticeKind {
  label: string;
  icon: NoticeIcon;
}

/** Where on the order a notice lands, and what the link says. */
interface NoticeDestination {
  /** The `?focus=` the submission profile reads, or none for the page top. */
  focus?: string;
  label: string;
}

interface NoticeMirrorEntry extends NoticeKind {
  destination: NoticeDestination;
  /** Where the notice's subject lives: an ORDER (the default), an INVOICE
   *  (`/surety/invoices/{id}` when the item names one, BRK-S187, else its
   *  order), the ACCOUNT (`/surety/accounts/{accountId}`), for a decision
   *  about the insured (BRK-S330), or a commercial SUBMISSION
   *  (`/submissions/{submissionId}`, BRK-S466). */
  subject?: "invoice" | "account" | "submission";
}

/** The link a notice with no mirror entry still gets when it names an order. */
const PROFILE_DESTINATION: NoticeDestination = {
  label: "View the submission →",
};

/** A commercial notice's landing: the submission's quotes, or its Actions
 *  tab (BRK-S464) for what Signal asks and closes. */
const QUOTE_DESTINATION: NoticeDestination = { label: "View the quote →" };
const ACTIONS_TAB = "actions";

/**
 * The broker notice types this build can name, what each reads as, and where
 * on the order each one lands.
 *
 * A MIRROR of PMS's closed `noticeType` vocabulary, filled consumer-first: a
 * type is added here BEFORE PMS emits it (BRK-S188 took all of wave 2's at
 * once), so PMS's emitters can deploy in any order afterwards. It is still a
 * lookup, never a union `noticeType` is narrowed to. A row of a type this build
 * has not heard of lists as the neutral `FALLBACK_NOTICE_KIND` and still links
 * to its order.
 *
 * `bid_result_needed` (BRK-S177) and `gia_needed` (BRK-S182) use the `?focus=`
 * PMS's email templates build, so both halves of one reminder land in the same
 * place. `bond_packet_ready` lands on the order's Documents section.
 *
 * The four invoicing notices land on `/surety/invoices/{invoiceId}` when the
 * item names its invoice (BRK-S187), and on the ORDER until it does.
 */
const NOTICE_MIRROR: Readonly<Record<string, NoticeMirrorEntry>> = {
  bid_result_needed: {
    label: "Bid result needed",
    icon: "bid",
    destination: { focus: "bid-result", label: "Record your bid result →" },
  },
  gia_needed: {
    label: "GIA needed",
    icon: "signature",
    destination: { focus: "gia", label: "Send the GIA for signature →" },
  },
  // BRK-S284 (PMS-S494): a won bid whose final price is over the single bond
  // or aggregate program limit, sent to Signal's underwriter. MUST-SEND, so its
  // preferences row is locked. The label says "under review" so a broker does
  // not read the row as the next step being theirs.
  over_limit_won_bid: {
    label: "Won bid under review",
    icon: "bid",
    destination: PROFILE_DESTINATION,
  },
  bond_packet_ready: {
    label: "Bond packet ready",
    icon: "packet",
    destination: { focus: "documents", label: "View the documents →" },
  },
  // BRK-S511 (PMS-S913): Signal asked for something on a bond order — a
  // document request or a question opened as an action item. MANDATORY
  // upstream. A requested document is a `required` row on the order's
  // Documents section, so that is where it lands.
  bond_info_requested: {
    label: "Signal needs something",
    icon: "update",
    destination: { focus: "documents", label: "See what’s needed →" },
  },
  invoice_generated: {
    label: "New invoice",
    icon: "invoice",
    destination: PROFILE_DESTINATION,
    subject: "invoice",
  },
  invoice_due: {
    label: "Invoice due",
    icon: "invoice",
    destination: PROFILE_DESTINATION,
    subject: "invoice",
  },
  ucc_warning_letter: {
    label: "UCC warning letter",
    icon: "warning",
    destination: PROFILE_DESTINATION,
    subject: "invoice",
  },
  invoice_paid: {
    label: "Invoice paid",
    icon: "paid",
    destination: PROFILE_DESTINATION,
    subject: "invoice",
  },
  // BRK-S323 (PMS-S718): the order was withdrawn — lands on its profile,
  // where the withdrawal, its reason and the status history read.
  submission_withdrawn: {
    label: "Submission withdrawn",
    icon: "update",
    destination: PROFILE_DESTINATION,
  },
  // BRK-S330 / BRK-S329 (PMS-S723): the underwriter's decision on a Signal
  // Advance application or a capacity request. Both concern the INSURED, so
  // they land on the account page, which shows the new limits or "Request not
  // approved" — never a reason (plan rule 25).
  advance_application_decided: {
    label: "Signal Advance decision",
    icon: "update",
    destination: { label: "View the account →" },
    subject: "account",
  },
  capacity_request_decided: {
    label: "Capacity request decided",
    icon: "update",
    destination: { label: "View the account →" },
    subject: "account",
  },
  // BRK-S466 — the commercial lifecycle (PMS-S872, PMS-S878 and after). Each
  // concerns a SUBMISSION, never a bond order, and lands on its page: the
  // quotes, or the Actions tab for what Signal asks and closes (BRK-S464).
  // Labels are nouns, so they read beside PMS's subject.
  quote_ready: {
    label: "Quote ready",
    icon: "quote",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  bind_request_decided: {
    label: "Bind request update",
    icon: "update",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  quote_referral_decided: {
    label: "Underwriter review decided",
    icon: "review",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  action_item_info_requested: {
    label: "Signal needs more information",
    icon: "reply",
    destination: { focus: ACTIONS_TAB, label: "Reply to Signal →" },
    subject: "submission",
  },
  action_item_closed: {
    label: "Request closed",
    icon: "reply",
    destination: { focus: ACTIONS_TAB, label: "View the request →" },
    subject: "submission",
  },
  subjectivity_update: {
    label: "Condition update",
    icon: "condition",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  commercial_submission_received: {
    label: "Submission received",
    icon: "update",
    destination: PROFILE_DESTINATION,
    subject: "submission",
  },
  quote_expires_soon: {
    label: "Quote expiring",
    icon: "quote",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  quote_expired: {
    label: "Quote expired",
    icon: "quote",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  binder_issued: {
    label: "Binder issued",
    icon: "bound",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  policy_issued: {
    label: "Policy issued",
    icon: "bound",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
  binder_expiring: {
    label: "Binder expiring",
    icon: "warning",
    destination: QUOTE_DESTINATION,
    subject: "submission",
  },
};

/** Every type the mirror names — the runtime copy the contract test diffs
 *  against the pinned vocabulary (read ahead of it, BRK-S284). */
export const MIRRORED_NOTICE_TYPES: readonly string[] =
  Object.keys(NOTICE_MIRROR);

/** What a row of a type the mirror does not name reads as. */
export const FALLBACK_NOTICE_KIND: NoticeKind = {
  label: "Update",
  icon: "update",
};

function mirrorEntry(noticeType: string | null): NoticeMirrorEntry | undefined {
  // `Object.hasOwn`, so a type spelled like a prototype key cannot match.
  return noticeType !== null && Object.hasOwn(NOTICE_MIRROR, noticeType)
    ? NOTICE_MIRROR[noticeType]
    : undefined;
}

/** What kind of notice a row is: the mirror's label and icon, or the neutral
 *  "Update" for a type this build has not heard of. Never blank. */
export function noticeKind(item: Pick<InboxItem, "noticeType">): NoticeKind {
  const entry = mirrorEntry(item.noticeType);
  return entry
    ? { label: entry.label, icon: entry.icon }
    : { ...FALLBACK_NOTICE_KIND };
}

/** Where an inbox row sends the broker, and what the link says. */
export interface NotificationDestination {
  to: string;
  label: string;
}

/**
 * Where an inbox row sends the broker, or **null** when there is nowhere
 * useful (BRK-S177).
 *
 * Pure, so the rule is testable without a render, and ONE place so the inbox
 * and any later surface cannot disagree about where a notice points.
 *
 * It needs the record's id. A row that does not say which order it is about
 * has nowhere to go, and inventing a destination (the list, the account) would
 * answer a different question than the one the broker clicked. Until PMS-S466
 * publishes `bondSubmissionId`, that is every live row, and no workaround is
 * built for it. A row that names its order but has a type the mirror does not
 * know links to that order's profile, with no `?focus=` (BRK-S188).
 *
 * A commercial notice lands on its submission (BRK-S466): the page, or its
 * Actions tab (`?tab=actions`) for what Signal asks and closes. A type the
 * mirror doesn't name still links to the submission when the row names one,
 * and a submission row never builds a surety path.
 */
export function notificationDestination(
  item: Pick<
    InboxItem,
    | "noticeType"
    | "bondSubmissionId"
    | "invoiceId"
    | "accountId"
    | "submissionId"
    | "subjectKind"
  >,
): NotificationDestination | null {
  const entry = mirrorEntry(item.noticeType);
  if (item.submissionId && (entry?.subject === "submission" || !entry)) {
    const page = `/submissions/${encodeURIComponent(item.submissionId)}`;
    const destination = entry?.destination ?? PROFILE_DESTINATION;
    return {
      to: destination.focus ? `${page}?tab=${destination.focus}` : page,
      label: destination.label,
    };
  }
  if (entry?.subject === "invoice" && item.invoiceId) {
    return {
      to: `/surety/invoices/${encodeURIComponent(item.invoiceId)}`,
      label: "View the invoice →",
    };
  }
  if (entry?.subject === "account" && item.accountId) {
    return {
      to: `/surety/accounts/${encodeURIComponent(item.accountId)}`,
      label: entry.destination.label,
    };
  }
  const id = item.bondSubmissionId;
  if (!id) return null;
  // An account notice with no account id falls back to its order's profile,
  // labelled as the order — never "View the account" over an order link.
  const destination =
    entry && entry.subject !== "account"
      ? entry.destination
      : PROFILE_DESTINATION;
  const path = `/surety/submissions/${encodeURIComponent(id)}`;
  return {
    to: destination.focus ? `${path}?focus=${destination.focus}` : path,
    label: destination.label,
  };
}

/** The destination's URL alone — see `notificationDestination`. */
export function notificationLink(
  item: Pick<
    InboxItem,
    | "noticeType"
    | "bondSubmissionId"
    | "invoiceId"
    | "accountId"
    | "submissionId"
    | "subjectKind"
  >,
): string | null {
  return notificationDestination(item)?.to ?? null;
}

/** The paged inbox envelope (the shared PMS list shape). */
export interface InboxPage {
  items: InboxItem[];
  total: number;
  limit: number;
  offset: number;
}

/** The NARROW engagement state read/archive return — never the delivery columns. */
export interface InboxItemState {
  id: string;
  seenAt: string | null;
  readAt: string | null;
  archivedAt: string | null;
}

export interface InboxQuery {
  limit?: number;
  offset?: number;
}

/** GET /api/broker/notifications — the broker's own inbox, newest first (paged).
 *  Rendering the returned rows marks them SEEN server-side (the badge clears). */
export function listNotifications(
  request: AuthedRequester,
  query: InboxQuery = {},
): Promise<InboxPage> {
  const qs = new URLSearchParams();
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.offset != null) qs.set("offset", String(query.offset));
  const s = qs.toString();
  return request<InboxPage>(`${BASE}${s ? `?${s}` : ""}`);
}

/** GET /api/broker/notifications/unread-count — the UNSEEN bell-badge count.
 *  (The route keeps the S353 name; the value is the unseen count.) */
export function getUnseenCount(request: AuthedRequester): Promise<number> {
  return request<{ count: number }>(`${BASE}/unread-count`).then(
    (r) => r.count,
  );
}

/** POST /api/broker/notifications/{id}/read — open one item (clears its bold). */
export function markNotificationRead(
  request: AuthedRequester,
  id: string,
): Promise<InboxItemState> {
  return request<InboxItemState>(`${BASE}/${id}/read`, { method: "POST" });
}

/** POST /api/broker/notifications/{id}/archive — dismiss one item from the feed. */
export function archiveNotification(
  request: AuthedRequester,
  id: string,
): Promise<InboxItemState> {
  return request<InboxItemState>(`${BASE}/${id}/archive`, { method: "POST" });
}

/** Unread = delivered but not opened yet (readAt null) — the list's bold rows. */
export function isUnread(item: Pick<InboxItem, "readAt">): boolean {
  return item.readAt === null;
}
